// src/encounter/left-panel-fold.tsx
// The encounter's LEFT PANEL fold (the patient summary column), copied from the clinician prototype
// (`RecordPatientRail` + `Shell`, measured 15 Oct; design log "Practitioner: the patient rail folds to nothing").
//
//   · Open the column is 320 wide; folded it takes NO room at all (no strip): the page gets the width.
//   · ONE toggle, a 24 px control on the page header's bottom hairline: open, a round ‹ on the column's right edge;
//     folded, a small pill (a file icon and ›, the patient's details are in there) centred under the patient's photo.
//   · Folding is local UI state, as in the prototype: the default is folded below 1280 wide, and a choice holds while
//     the encounter is on screen (the pre-encounter and the started visit share it) but is not stored.
//
// The look lives in `showcase-bridge.css` under `/* encounter left panel */`; the width is ONE animated custom property
// (`--enc-left-width`) that the pre-encounter rail, the started visit's chart column and the toggle all read, so they move
// together. This file only holds the state and the control.
import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/** The prototype folds the panel below this width (`Shell`: `useState(() => viewport < 1280)`). */
export const LEFT_PANEL_FOLD_BELOW = 1280;

type Listener = () => void;
const listeners = new Set<Listener>();
let folded: boolean | null = null;
let lastWide: boolean | null = null;

function isWide(): boolean {
  return typeof window === 'undefined' || window.innerWidth >= LEFT_PANEL_FOLD_BELOW;
}

function read(): boolean {
  if (folded === null) {
    lastWide = isWide();
    folded = !lastWide;
  }
  return folded;
}

function emit() {
  listeners.forEach((l) => l());
}

/** Set the fold (the toggle, or the D-41 rule that opening Scrips Intelligence below 1280 folds the chart). */
export function setLeftPanelFolded(next: boolean): void {
  if (read() === next) return;
  folded = next;
  emit();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test seam: forget the choice so the next read starts from the width again. */
export function resetLeftPanelFold(): void {
  folded = null;
  lastWide = null;
  emit();
}

/** The prototype re-applies the width rule on a resize; here only when the width CROSSES the breakpoint, so a drag
 *  that stays on one side keeps the clinician's choice. */
/** How many zoning consumers are mounted (`?x=intel-always-open`, encounter-zoning.ts). While one is, the zoning decides the
 *  fold from the measured canvas and this file's 1280 resize rule stands aside; with none (the flag on but no zoning on
 *  screen, e.g. a pre-visit without the rail), the rule runs as before. */
let zoningConsumers = 0;
export function setLeftPanelZoningActive(active: boolean): void {
  zoningConsumers = Math.max(0, zoningConsumers + (active ? 1 : -1));
}

function onResize() {
  if (zoningConsumers > 0) return;
  const wide = isWide();
  if (lastWide === null) lastWide = wide;
  if (wide === lastWide) return;
  lastWide = wide;
  folded = !wide;
  emit();
}

/** The fold state and its setter. While a component that calls this is mounted, the state is mirrored onto the root
 *  (`data-enc-left="folded"`), which is what the bridge's width variable and the toggle read. */
export function useLeftPanelFold(): { folded: boolean; setFolded: (next: boolean) => void; toggle: () => void } {
  const value = useSyncExternalStore(subscribe, read, () => false);
  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    const root = document.documentElement;
    if (value) root.dataset.encLeft = 'folded';
    else root.dataset.encLeft = 'open';
    return () => {
      delete root.dataset.encLeft;
    };
  }, [value]);
  return { folded: value, setFolded: setLeftPanelFolded, toggle: () => setLeftPanelFolded(!read()) };
}

/** The panel's one toggle, Place it inside a
 *  `position: relative` box whose bottom edge is the header's hairline and say which with `placement`. Open: round ‹.
 *  Folded: the file + › pill. Both carry an accessible name, so the app-wide instant tooltip names them (no `title`). */
export function LeftPanelToggle({
  folded: isFolded,
  onToggle,
  indicator,
  placement = 'header',
}: {
  folded: boolean;
  onToggle: () => void;
  /** `header`: in a box that starts at the frame edge (the pre-encounter's page header). `tabs`: in the started visit's
   *  tab row, whose box starts at the column's right edge and moves with it. */
  placement?: 'header' | 'tabs';
  /** An optional small marker on the folded pill (the started visit keeps the allergy dot of its old strip). */
  indicator?: ReactNode;
}) {
  // `?x=rail-v3` (Antonin's Folded board): the same 24 px white circle with a hairline folded and open; only the chevron flips.
  const railV3 = isEncounterFlagOn('railV3');
  return (
    <button
      type="button"
      data-rail-v3={railV3 ? 'true' : undefined}
      data-slot="enc-left-toggle"
      data-folded={isFolded ? 'true' : 'false'}
      data-placement={placement}
      aria-label={isFolded ? 'Show patient summary' : 'Collapse the patient summary'}
      aria-expanded={!isFolded}
      onClick={onToggle}
      className="enc-left-toggle"
    >
      {isFolded ? (
        <>
          {railV3 ? null : <FileText aria-hidden data-glyph="panel" />}
          <ChevronRight aria-hidden />
          {indicator}
        </>
      ) : (
        <ChevronLeft aria-hidden />
      )}
    </button>
  );
}
